// Banner rendered at the top of every tab
// on a Closed Deal. Pure presentation; the actual read-only enforcement
// lives server-side in ClosedDealReadOnlyMiddleware.

import { CheckCircle2, ExternalLink, Lock } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import type { DealDetailDto } from "@/types/deals";

interface ClosedDealBannerProps {
  deal: DealDetailDto;
  /** When true the banner renders as the in-tab success surface (Step 4
   *  post-success state); otherwise it's the slim top-of-tab banner. */
  embedded?: boolean;
}

export function ClosedDealBanner({ deal, embedded = false }: ClosedDealBannerProps) {
  const { hasPermission } = useAuth();
  const canReopen = hasPermission("DealFlow.ReopenClosedDeal");
  const { date } = useDateFormatter();

  if (deal.stage !== "Closed") return null;

  const loanNumber = deal.loanNumber ?? "(loan number unavailable)";
  const closedAt = deal.closedAt
    ? date(deal.closedAt)
    : "(date unavailable)";

  if (embedded) {
    return (
      <div
        className="border border-status-success/40 bg-status-success/5 rounded-md p-space-4"
        data-testid="closed-deal-banner-embedded"
      >
        <div className="flex items-start gap-space-3">
          <CheckCircle2 size={28} className="text-status-success shrink-0" aria-hidden />
          <div className="flex-1">
            <h3 className="font-display text-h3 text-onyx m-0">
              Loan {loanNumber} created
            </h3>
            <p className="text-small text-onyx m-0 mt-space-2">
              This deal is now Closed (created Loan {loanNumber} on {closedAt}).
              All tabs are read-only. Continue work in Loan Servicing.
            </p>
            <div className="mt-space-3 flex items-center gap-space-2 flex-wrap">
              {deal.loanId && (
                <a
                  href={`/portfolio-admin/loans/${deal.loanId}`}
                  className="inline-flex items-center gap-space-1 text-gold-shadow underline"
                >
                  Continue in Loan Servicing
                  <ExternalLink size={12} aria-hidden />
                </a>
              )}
              {canReopen && (
                <Button variant="secondary" size="sm" disabled>
                  Reopen deal (Admin)
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="border border-gold/40 bg-gold/5 rounded-md px-space-3 py-space-2 flex items-center gap-space-3"
      data-testid="closed-deal-banner"
      role="status"
    >
      <Lock size={18} className="text-gold-shadow shrink-0" aria-hidden />
      <div className="flex-1 min-w-0 text-small text-onyx">
        This deal is Closed (created Loan <strong>{loanNumber}</strong> on{" "}
        {closedAt}). All tabs are read-only.{" "}
        {deal.loanId && (
          <a
            href={`/portfolio-admin/loans/${deal.loanId}`}
            className="text-gold-shadow underline"
          >
            Continue work in Loan Servicing →
          </a>
        )}
      </div>
      {canReopen && (
        <Button variant="secondary" size="sm" disabled>
          Reopen deal (Admin)
        </Button>
      )}
    </div>
  );
}
